import {
  exclusiveEndToInclusiveDate,
  utcDateInputValue,
} from "../../../../utils/calendarTime";

/**
 * Human date ranges for the itinerary surfaces (catalog, shared-events banner, staff queue).
 *
 * Pure and dependency-free so every surface that has to name "when is this tournament?" says it
 * the same way, and so the rule that trips people up — an all-day event's end is EXCLUSIVE, a
 * request's end is INCLUSIVE — is written down once instead of re-derived per component.
 */

/** "Jul 16, 2026" for a YYYY-MM-DD day key. Returns the key unchanged if it won't parse. */
export function formatDayKey(dayKey: string): string {
  const date = new Date(`${dayKey}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return dayKey;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/**
 * "Jul 16, 2026" for a single day, "Jul 16, 2026 – Jul 19, 2026" for a span.
 *
 * `endExclusive` is the all-day calendar-event contract: `endsAtUtc` is 00:00 UTC of the day AFTER
 * the last day, so it is backed off one day before rendering. Pass false for an already-inclusive
 * end such as an itinerary request's `endDateUtc`.
 */
export function formatUtcDateRange(
  startIso: string,
  endIso: string,
  endExclusive: boolean,
): string {
  const startKey = utcDateInputValue(startIso);
  const endKey = endExclusive
    ? exclusiveEndToInclusiveDate(endIso)
    : utcDateInputValue(endIso);
  const start = formatDayKey(startKey);
  if (startKey === endKey) return start;
  return `${start} – ${formatDayKey(endKey)}`;
}

/**
 * The same range for an event-shaped value, reading `isAllDay` to decide whether the end is
 * exclusive. A timed event's end is a true instant on the last day, so it is inclusive already.
 */
export function formatEventDateRange(event: {
  startsAtUtc: string;
  endsAtUtc: string;
  isAllDay: boolean;
}): string {
  return formatUtcDateRange(event.startsAtUtc, event.endsAtUtc, event.isAllDay);
}

/**
 * The dates of an itinerary REQUEST, either of which may be missing.
 *
 * A coordinator often commits to a tournament months before its schedule is published, so the
 * request form asks only for a name. Every surface that shows a request — the coach's
 * confirmation, RosterPilot's queue — therefore has to say "we don't know yet" out loud rather
 * than render an empty range or a bogus epoch date.
 */
export function formatRequestDateRange(
  startIso: string | null,
  endIso: string | null,
): string {
  if (startIso && endIso) return formatUtcDateRange(startIso, endIso, false);
  if (startIso) return `from ${formatDayKey(utcDateInputValue(startIso))} (end date not given)`;
  if (endIso) return `until ${formatDayKey(utcDateInputValue(endIso))} (start date not given)`;
  // Phrased as a to-do, not as a fault: this string reaches the coach's own confirmation as well
  // as RosterPilot's queue, and "not given" reads there as though they left something out.
  return "dates to be confirmed";
}
